import { Component, EventEmitter, HostListener, Input, OnChanges, Output, ViewEncapsulation } from '@angular/core';
import { KtdGridLayoutItem } from '@katoid/angular-grid-layout';
import { CallState } from '@lcs/shared/api';
import { LcsBreakpoints, LcsScreensizeBreakpointsService } from '@lcs/shared/breakpoints';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgChanges } from 'src/lib/ng-changes';
import {
   compactLayout,
   constructDashboardTileLayoutConfigurationInput,
   mapLayoutToTargetColumns,
} from '../models/dashboard-layout-utils';
import { Dashboard } from '../models/dashboard.model';
import {
   reloadDashboardAfterResize,
   reloadDashboardForNewLayout,
   setCurrentDashboardColumnLayoutSize,
} from '../state/dashboard.actions';

export interface DashboardGridLayoutItem extends Omit<KtdGridLayoutItem, 'id'> {
   id: number;
   dashboardTileId: number;
}
export interface DashboardGridLayoutItemConfigurationInput extends DashboardGridLayoutItem {
   isDirty?: boolean;
   fields: string[];
}
@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-dashboard-view',
   styleUrls: ['./dashboard-view.component.scss'],
   templateUrl: './dashboard-view.component.html',
   standalone: false,
})
export class DashboardViewComponent implements OnChanges {
   @Input() adminToken: string;

   @Input() set selectedDashboard(dashboard: Dashboard | null) {
      this._selectedDashboard = dashboard;
   }
   @HostListener('window:resize', ['$event'])
   onResize(event) {
      // Triggers change detection so katoid grid can reposition and resize grid items on window resize
      this.displayLayout = [...this.displayLayout];
      this.store.dispatch(
         reloadDashboardAfterResize({
            layout: [...this.getDisplayLayout()],
         })
      );
   }

   get selectedDashboard(): Dashboard | null {
      return this._selectedDashboard;
   }

   @Input() callState: CallState;

   @Input() preservedLayout: DashboardGridLayoutItem[] | null = null;

   @Input() dashboards: Dashboard[] | undefined;

   @Input() isEditing = false;

   @Output() addDashboard = new EventEmitter<void>();

   @Output() addTile = new EventEmitter<void>();

   @Output() layoutUpdated = new EventEmitter<{
      dashboardTileLayoutConfigurationInput: DashboardGridLayoutItemConfigurationInput[];
      columnLayoutSize: number;
   }>();

   trackById = (index: number, item: DashboardGridLayoutItem) => item.id;

   cols = 6;

   rowHeight = 250;

   displayLayout: DashboardGridLayoutItem[] = [];

   private _selectedDashboard: Dashboard | null;

   private currentBreakpoint: LcsBreakpoints | null = null;

   private destroyed$ = new Subject<void>();

   constructor(private screensizeBreakpointsService: LcsScreensizeBreakpointsService, private store: Store) {
      this.screensizeBreakpointsService
         .screensizeChange()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((breakpoint: LcsBreakpoints | null) => {
            if (breakpoint !== this.currentBreakpoint) {
               this.currentBreakpoint = breakpoint;

               if (this.currentBreakpoint === LcsBreakpoints.Small) this.cols = 1;
               else if (this.currentBreakpoint === LcsBreakpoints.Medium) this.cols = 2;
               else this.cols = 6;

               this.triggerLayoutMapping();
               this.store.dispatch(
                  setCurrentDashboardColumnLayoutSize({ currentDashboardColumnLayoutSize: this.cols })
               );
            }
         });
   }

   ngOnChanges(changes: NgChanges<DashboardViewComponent>): void {
      if (changes.preservedLayout) {
         if (this.cols !== this.selectedDashboard?.lastSavedColumnLayoutSize) {
            this.triggerLayoutMapping();
         }
      }
      if (this.selectedDashboard) {
         this.displayLayout = this.getDisplayLayout();
      }
   }

   private getDisplayLayout(): DashboardGridLayoutItem[] {
      if (!this.selectedDashboard) return [];

      const preservedCols = this.selectedDashboard?.lastSavedColumnLayoutSize ?? 1;
      const compactedPreservedLayout = this.preservedLayout
         ? compactLayout(this.preservedLayout, 'vertical', preservedCols)
         : [];

      if (this.cols === preservedCols) {
         return [...compactedPreservedLayout];
      }

      return mapLayoutToTargetColumns(preservedCols, this.cols, compactedPreservedLayout);
   }

   private triggerLayoutMapping(): void {
      //TODO
      this.store.dispatch(
         reloadDashboardForNewLayout({
            layout: [...this.getDisplayLayout()],
         })
      );
   }

   onAddDashboard() {
      this.addDashboard.emit();
   }

   onAddTile() {
      this.addTile.emit();
   }

   onLayoutUpdated(newLayout: DashboardGridLayoutItem[]) {
      // Find dirty tiles and what fields they are dirty on
      let dashboardTileLayoutConfigurationInput: DashboardGridLayoutItemConfigurationInput[] =
         constructDashboardTileLayoutConfigurationInput(newLayout, this.preservedLayout);

      // Mark all tiles as dirty when moving from smaller layout to larger in the event switching a width of a tile from its bigger mapping to the
      // previous smaller size preventing a dirty field from being marked and the save not working properly
      if (this.selectedDashboard && this.selectedDashboard.lastSavedColumnLayoutSize < this.cols) {
         dashboardTileLayoutConfigurationInput = dashboardTileLayoutConfigurationInput.map((dtci) => {
            if (this.selectedDashboard) {
               const widthMatchSelectedDashboard = dtci.w === this.selectedDashboard?.lastSavedColumnLayoutSize;
               const widthMatchPreservedLayout =
                  dtci.w === this.preservedLayout?.find((l) => l.dashboardTileId === dtci.dashboardTileId)?.w;
               if (widthMatchSelectedDashboard && widthMatchPreservedLayout) {
                  return { ...dtci, isDirty: true, fields: ['x', 'y', 'w', 'h'] };
               }
            }
            return dtci;
         });
      }

      if (dashboardTileLayoutConfigurationInput.some((dti) => dti.isDirty)) {
         this.layoutUpdated.emit({ dashboardTileLayoutConfigurationInput, columnLayoutSize: this.cols });
      }
   }
}
